import React from 'react'
import { Check, ExternalLink, Inbox } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button as UIButton } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import Button from '../button'
import CallButton from './buttons/call'
import SmsButton from './buttons/sms'
import EmailButton from '../emailer/email_button'
import _ from 'underscore'
import CalendarTime from '../calendar_time'
import RelatedResources from '../related_resources/index'
import CoachbitOrders from '../coachbit_orders/index'
import LeadLabels from './labels/index'
import LocalTime from '../local_time'
import WhatsappButton from '../whatsapper/whatsapp_button'
import CallOutcome from './call_outcome'
import SvgIcon from '../svg_icon'
import store from '../../state/store'
import CountryFlag from '@/components/CountryFlag'

class LeadHeader extends React.Component {

  onToggleDeactivated = (reason = '') => {
    if (this.props.lead.workflow_state == 'deactivated')
      store.emit('lead:activate', this.props.lead.id)
    else
      store.emit('lead:deactivate', this.props.lead.id, reason)
  }

  onConvertButtonClick = () => {
    if (!this.props.lead.converted) {
      store.emit('lead:convert', this.props.lead.id)
    }
  }

  onIpAddressClick = () => {
    store.emit('lead:lookup_ip', this.props.lead.id)
  }

  onFetchScore = () => {
    store.emit('lead:lookup_score', this.props.lead.id)
  }

  onSelectCallOutcome = (value) => {
    store.emit('lead:track_call_outcome', this.props.lead.id, value)
  }

  onOpenEmail = () => {
    store.emit('ui:mailer:open', {
      related_to_type: 'Lead',
      related_to_id: this.props.lead.id,
      recipient_name: this.props.lead.settings.enquirer_name
    })
  }

  onOpenWhatsapp = () => {
    store.emit('ui:whatsapper:open', {
      related_to_type: 'Lead',
      related_to_id: this.props.lead.id,
      recipient_type: 'Customer',
      recipient_id: this.props.lead.customer_id,
      recipient_name: this.props.lead.settings.enquirer_name
    })
  }

  render() {
    const lead = this.props.lead

    return (
      <div>
        <div className="flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            {lead.settings.enquirer_name ?
              <h4 className="font-heading text-xl font-medium leading-tight">
                {lead.settings.enquirer_name}
                &nbsp;
                {(!_.isEmpty(lead.related_resources)) ?
                  <RelatedResources related_resources={lead.related_resources}/>
                  : null
                }
                &nbsp;
                <CoachbitOrders coachbit_orders={lead.coachbit_orders} link={lead.links.customer}/>
              </h4>
              : null
            }
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Badge variant="secondary" className="cursor-pointer" onClick={this.onFetchScore}>Score: {lead.score}</Badge>
              {lead.links.enquiry ?
                <Badge asChild variant="secondary">
                  <a href={lead.links.enquiry} target="_blank">Enquiry {lead.sales_enquiry_id}</a>
                </Badge>
                : null
              }
              {lead.links.customer ?
                <Badge asChild variant="secondary">
                  <a href={lead.links.customer} target="_blank">Customer {lead.customer_id}</a>
                </Badge>
                : null
              }
              {lead.country ?
                <Badge variant="secondary" className="cursor-pointer" onClick={this.onIpAddressClick}>
                  <CountryFlag country_code={lead.country_code}/> {lead.country.name}
                </Badge>
                : null
              }
              {lead.local_time ?
                <LocalTime local_time={lead.local_time}/>
                : null
              }
              {(lead.labels.length > 0) ?
                <LeadLabels labels={lead.labels} />
                : null
              }
              {_.map(lead.scheduled_calls, (scheduled_call) => {
                return (
                  <Badge key={scheduled_call.id}>
                    {scheduled_call.manager_name} to call <CalendarTime date={scheduled_call.start_time}/>
                  </Badge>
                )
              })}
            </div>
          </div>
          <div className="ml-auto">
            <div className="flex flex-wrap items-center justify-end gap-1.5">
              <CallOutcome onSelect={this.onSelectCallOutcome}/>
              <UIButton asChild variant="secondary" size="sm">
                <a target="_blank" href={lead.links.tutor_browser}>
                  Browse Tutors <ExternalLink className="size-3.5"/>
                </a>
              </UIButton>
              {(lead.workflow_state == 'deactivated') ?
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <UIButton variant="destructive" size="sm">Deactivated</UIButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>Cancel</DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => this.onToggleDeactivated()}>Activate</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                :
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <UIButton variant="secondary" size="sm">Deactivate</UIButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>Cancel</DropdownMenuItem>
                    {_.map(['no response','fees too high','beyond the scope of our services','no tutor','duplicate enquiry','other'], (deactivation_reason) => {
                      return (
                        <DropdownMenuItem key={deactivation_reason} variant="destructive" onSelect={() => this.onToggleDeactivated(deactivation_reason)}>
                          Deactivate - {deactivation_reason}
                        </DropdownMenuItem>
                      )
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              }
              {lead.converted ?
                <Button className="btn btn-success btn-sm" onClick={this.onConvertButtonClick}>
                  <Check className="size-4"/> Converted
                </Button>
                :
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <UIButton variant="secondary" size="sm">Convert Lead</UIButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem>Cancel</DropdownMenuItem>
                    <DropdownMenuItem variant="destructive" onSelect={this.onConvertButtonClick}>Convert</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              }
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
              <CallButton mobile_number={lead.settings.enquirer_contact_number} />
              <UIButton asChild size="icon-sm" variant="secondary" title="Open in Customer.io">
                <a href={lead.links.customerio} target="_blank">
                  <SvgIcon icon="customerio"/>
                </a>
              </UIButton>
              {lead.links.messagebird &&
                <UIButton
                  asChild
                  size="icon-sm"
                  variant="secondary"
                  title={lead.links.messagebird_open ? 'Open ticket in Inbox' : 'Open in Inbox'}
                  className={lead.links.messagebird_open ? 'ring-1 ring-sky-400/50 shadow-[0_0_6px_1px_rgba(56,189,248,0.3)]' : undefined}
                >
                  <a href={lead.links.messagebird} target="_blank" rel="noopener noreferrer">
                    <Inbox/>
                  </a>
                </UIButton>
              }
              <WhatsappButton onClick={this.onOpenWhatsapp}/>
              <SmsButton store={store}/>
              <EmailButton onEmail={this.onOpenEmail}/>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default LeadHeader
